import { _ } from '@sveltia/i18n';

import { fetchAPI, fetchGraphQL, graphqlVars } from '$lib/services/backends/git/shared/api';
import { REPOSITORY_INFO_PLACEHOLDER } from '$lib/services/backends/git/shared/repository';
import { user } from '$lib/services/user/account.svelte';

/**
 * @import { RepositoryBaseURLs, RepositoryInfo, User } from '$lib/types/private';
 */

/** @type {RepositoryInfo} */
export const repository = { ...REPOSITORY_INFO_PLACEHOLDER };

/**
 * Generate base URLs for accessing the repository’s resources.
 * @param {string} repoURL The base URL of the repository.
 * @param {string} [branch] The branch name. Could be `undefined` if the branch is not specified in
 * the CMS configuration.
 * @returns {RepositoryBaseURLs} An object containing the tree base URL for browsing files, and the
 * blob base URL for accessing file contents.
 */
export const getBaseURLs = (repoURL, branch) => ({
  treeBaseURL: branch ? `${repoURL}/-/tree/${branch}` : repoURL,
  blobBaseURL: branch ? `${repoURL}/-/blob/${branch}` : '',
  commitBaseURL: `${repoURL}/-/commit`,
});

/**
 * Check if the user has access to the current repository.
 * @throws {Error} If the user is not a collaborator of the repository.
 * @see https://docs.gitlab.com/api/projects/#list-all-members-of-a-project
 * @see https://docs.gitlab.com/api/service_accounts/#list-all-project-service-accounts
 */
export const checkRepositoryAccess = async () => {
  const { owner, repo } = repository;
  const { id, login, bot } = /** @type {User} */ (user.account);
  const baseURL = `/projects/${encodeURIComponent(`${owner}/${repo}`)}`;
  const url = bot ? `${baseURL}/service_accounts` : `${baseURL}/users?search=${login}`;

  const response = /** @type {Response} */ (
    await fetchAPI(url, {
      headers: { Accept: 'application/json' },
      responseType: 'raw',
    })
  );

  const users = response.ok ? /** @type {{ id: number }[]} */ (await response.json()) : [];

  if (!users.some((u) => u.id === id)) {
    throw new Error('Not a collaborator of the repository', {
      cause: new Error(_('repository_no_access', { values: { repo } })),
    });
  }
};

const FETCH_DEFAULT_BRANCH_NAME_QUERY = `
  query($fullPath: ID!) {
    project(fullPath: $fullPath) {
      repository {
        rootRef
      }
    }
  }
`;

/**
 * Fetch the repository’s default branch name, which is typically `master` or `main`.
 * @returns {Promise<string>} Branch name.
 * @throws {Error} When the repository could not be found, or when the repository is empty.
 * @see https://docs.gitlab.com/api/graphql/reference/#repository
 */
export const fetchDefaultBranchName = async () => {
  const { repo, repoURL = '' } = repository;

  const result = /** @type {{ project: { repository?: { rootRef: string } } }} */ (
    await fetchGraphQL(FETCH_DEFAULT_BRANCH_NAME_QUERY)
  );

  if (!result.project) {
    throw new Error('Failed to retrieve the default branch name.', {
      cause: new Error(_('repository_not_found', { values: { repo } })),
    });
  }

  const { rootRef: branch } = result.project.repository ?? {};

  if (!branch) {
    throw new Error('Failed to retrieve the default branch name.', {
      cause: new Error(_('repository_empty', { values: { repo } })),
    });
  }

  Object.assign(repository, { branch }, getBaseURLs(repoURL, branch));
  Object.assign(graphqlVars, { branch });

  return branch;
};
